import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { DateTime } from 'luxon';
import { employerApi } from '../services/apiClient';
import { toast } from 'react-toastify';

interface PauseApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  application: any | null;
  onSuccess: () => void;
}

const PauseApplicationDialog = ({ open, onClose, application, onSuccess }: PauseApplicationDialogProps) => {
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [resumeDate, setResumeDate] = useState<DateTime | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const calculateResumeDate = (months: number) => {
    return DateTime.now()
      .plus({ months: months + 1 })
      .set({ day: 1 });
  };

  const handleDurationSelect = (months: number) => {
    setSelectedDuration(months);
    setResumeDate(calculateResumeDate(months));
  };

  const handleSubmit = async () => {
    if (!selectedDuration || !application) return;

    setIsSubmitting(true);
    try {
      await employerApi.post(`/applications/${application.id}/pause`, {
        pauseDuration: selectedDuration
      });
      onSuccess();
    } catch (error: any) {
      console.error('Error pausing application:', error);
      toast.error(error?.message || 'Failed to pause application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedDuration(null);
    setResumeDate(null);
    onClose();
  };

  if (!application) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Pause Application</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity="info">
            You can pause this application for up to 3 months. During this time, the employee will not be charged and will not receive vouchers.
          </Alert>

          <Typography variant="body2">
            The application will automatically resume on the first day of the month after the pause period ends.
          </Typography>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Select pause duration:
            </Typography>
            <Stack spacing={1}>
              {[1, 2, 3].map((months) => (
                <Button
                  key={months}
                  variant={selectedDuration === months ? 'contained' : 'outlined'}
                  onClick={() => handleDurationSelect(months)}
                  disabled={isSubmitting}
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.5,
                    ...(selectedDuration === months && {
                      bgcolor: '#86eb85',
                      '&:hover': { bgcolor: '#72d771' }
                    })
                  }}
                >
                  {months} {months === 1 ? 'month' : 'months'}
                </Button>
              ))}
            </Stack>
          </Box>

          {resumeDate && (
            <Alert severity="success">
              The application will automatically resume on{' '}
              <strong>{resumeDate.toFormat('d MMMM yyyy')}</strong>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedDuration || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          sx={{ bgcolor: '#86eb85', '&:hover': { bgcolor: '#72d771' } }}
        >
          {isSubmitting ? 'Pausing...' : 'Confirm Pause'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PauseApplicationDialog;

